<template>
  <div class="bg-[#3A2821] relative pt-16 px-4 lg:px-0 overflow-hidden">
    <!-- Decorative arrows with animations -->
    <div class="absolute left-32 top-20 opacity-20 hidden lg:block">
      <svg
        width="120"
        height="120"
        viewBox="0 0 120 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="w-32 h-32 animate-float"
      >
        <path
          d="M90 30C60 45 40 75 30 90C45 60 75 40 90 30Z"
          stroke="white"
          stroke-width="2"
          fill="none"
        />
        <path d="M30 90C35 85 80 40 90 30" stroke="white" stroke-width="2" fill="none" />
      </svg>
    </div>
    <div class="absolute right-32 top-20 opacity-20 hidden lg:block">
      <svg
        width="120"
        height="120"
        viewBox="0 0 120 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="w-32 h-32 animate-float"
      >
        <path
          d="M30 30C60 45 80 75 90 90C75 60 45 40 30 30Z"
          stroke="white"
          stroke-width="2"
          fill="none"
        />
        <path d="M90 90C85 85 40 40 30 30" stroke="white" stroke-width="2" fill="none" />
      </svg>
    </div>

    <div class="max-w-7xl mx-auto text-center">
      <h1 class="font-bold text-white text-4xl lg:text-6xl">
        {{ data.title }}
        <span class="inline-block ml-2">
          <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6 lg:w-8 lg:h-8 animate-sparkle"
          >
            <path
              d="M16 0L18.5 13.5L32 16L18.5 18.5L16 32L13.5 18.5L0 16L13.5 13.5L16 0Z"
              fill="#FFD700"
            />
          </svg>
        </span>
      </h1>
      <p class="text-white text-base lg:text-lg mt-4 max-w-4xl mx-auto">{{ data.description }}</p>
    </div>

    <!-- Pricing Cards Container -->
    <div class="flex flex-col lg:flex-row justify-center gap-4 lg:gap-8 mt-12 relative pb-8">
      <!-- Background Split -->
      <div class="absolute inset-x-0 bottom-0 lg:bg-[#f7ecdf] h-1/2"></div>

      <!-- Pricing Cards -->
      <div
        v-for="card in data.cards"
        :key="card.type"
        :class="[
          'p-6 lg:p-8 rounded-2xl w-full h-fit lg:w-[384px] z-10 bg-white border-2 relative',
          card.type === 'standard' ? 'border-[#A66433]' : 'border-[#3A2821]/20',
        ]"
      >
        <!-- Most Popular Badge for Standard -->
        <div
          v-if="card.type === 'standard'"
          class="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#A66433] text-white px-4 py-1 rounded-full text-sm font-medium"
        >
          Most Popular
        </div>

        <p :class="['text-sm', card.type === 'standard' ? 'text-[#A66433]' : 'text-[#3A2821]']">
          {{ card.label }}
        </p>
        <h2
          :class="[
            'font-bold text-2xl lg:text-3xl mt-2',
            card.type === 'standard' ? 'text-[#A66433]' : 'text-[#3A2821]',
          ]"
        >
          {{ card.price ? `€ ${card.price}` : card.title }}
        </h2>
        <ul class="mt-6 space-y-3">
          <li
            v-for="feature in [...card.features, ...(card.unavailableFeatures || [])]"
            :key="feature"
            class="flex gap-3 items-center text-sm lg:text-base"
            :class="[card.features.includes(feature) ? 'text-[#3A2821]' : 'text-[#3A2821]/40']"
          >
            <div class="flex items-center gap-2">
              <template v-if="card.features.includes(feature)">
                <img
                  src="@/assets/images/checkmark.png"
                  alt="included"
                  class="w-4 h-4 bg-white rounded-full"
                />
              </template>
              <template v-else>
                <svg
                  class="w-4 h-4 text-[#3A2821]/40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </template>
            </div>
            {{ feature }}
          </li>
        </ul>
        <button
          @click="handleButtonClick(card.buttonLink || '/events')"
          class="bg-[#2D1B15] text-white font-semibold px-8 py-4 rounded-lg shadow hover:bg-[#3a241c] transition flex items-center justify-center gap-2"
          :class="[
            'rounded-full w-full py-3 mt-8 transition-colors duration-200',
            card.type === 'standard'
              ? 'bg-[#A66433] text-white hover:bg-[#8B5429]'
              : 'bg-[#3A2821] text-white hover:bg-[#2C1E1A]',
          ]"
        >
          {{ card.buttonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAuth } from '@/composables/useAuth'
import { useRouter } from 'vue-router'

const { isAuthenticated } = useAuth()

defineProps<{ data: Record<string, any> }>()

const router = useRouter()

const handleButtonClick = (link: string) => {
  if (isAuthenticated) {
    router.push(link)
  } else {
    router.push('/login')
  }
}
</script>

<style scoped>
.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-sparkle {
  animation: sparkle 2s ease-in-out infinite;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes sparkle {
  0%,
  100% {
    transform: scale(1) rotate(0deg);
  }
  50% {
    transform: scale(1.1) rotate(15deg);
  }
}
</style>
